import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { MemberTutorialKey } from '@mlm/shared';
import { useCartStore, type CartLine } from '../../storefront/lib/cart-store';

/** `live` walks the member through it for real; `practice` can change nothing. */
export type TutorialMode = 'live' | 'practice';

export type ActiveTutorial = {
  userId: string;
  key: MemberTutorialKey;
  mode: TutorialMode;
  /** Steps the member has pressed Next on. */
  seen: string[];
  /** The closing step has been reached, so its outcome is already recorded. */
  finished: boolean;
  /**
   * Practice lets the member press a real Buy now, which changes the cart. This
   * is the cart from before, put back when the practice ends.
   */
  cartBefore: Record<string, CartLine> | null;
};

type State = {
  active: ActiveTutorial | null;
  /**
   * Real runs ended in this browser session, so a failed save can't restart one
   * in a loop. A practice run doesn't count: it says nothing about the real one.
   */
  ended: MemberTutorialKey[];
  menuOpen: boolean;
  start: (args: { userId: string; key: MemberTutorialKey; mode: TutorialMode }) => void;
  acknowledge: (stepId: string) => void;
  markFinished: () => void;
  stop: () => void;
  setMenuOpen: (open: boolean) => void;
};

/**
 * Which tutorial is running. Tab-scoped (sessionStorage), so it survives a reload
 * and a trip to a payment provider's page. How a tutorial ENDED lives on the
 * server (`users.tutorial_state`), which is what follows the member to another
 * device.
 */
export const useTutorialStore = create<State>()(
  persist(
    (set, get) => ({
      active: null,
      ended: [],
      menuOpen: false,
      start: ({ userId, key, mode }) =>
        set((s) => ({
          active: {
            userId,
            key,
            mode,
            seen: [],
            finished: false,
            cartBefore: mode === 'practice' ? useCartStore.getState().lines : null,
          },
          ended: s.ended.filter((k) => k !== key),
        })),
      acknowledge: (stepId) =>
        set((s) =>
          s.active && !s.active.seen.includes(stepId)
            ? { active: { ...s.active, seen: [...s.active.seen, stepId] } }
            : s,
        ),
      markFinished: () => set((s) => (s.active ? { active: { ...s.active, finished: true } } : s)),
      stop: () => {
        const active = get().active;
        if (active?.cartBefore) useCartStore.getState().restoreLines(active.cartBefore);
        set((s) => ({
          active: null,
          ended:
            active?.mode === 'live' && !s.ended.includes(active.key)
              ? [...s.ended, active.key]
              : s.ended,
        }));
      },
      setMenuOpen: (menuOpen) => set({ menuOpen }),
    }),
    {
      name: 'member.tutorial',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({ active: s.active, ended: s.ended }),
    },
  ),
);
